/**
 * A SCENARIO ROW THAT BRANCHES ON A COMPARISON OF ITS OWN.
 *
 * The scenario picks the row; the row's own equation picks the answer. Built for a kinematics
 * stem with eight prose sign-combinations, four of them determinate and four that turn on which
 * magnitude is larger: the signs are PROSE, so `{kayak}` is positive in every variant and one
 * block-level equation cannot tell `+ - +` from `- + -`. The row knows its own signs —
 * `{kayak} - {river}` belongs to one row and `{river} - {kayak}` to its mirror.
 *
 * ## Why this is a hook rather than five copies
 *
 * MCQ was written by hand first, deliberately, because two pagers in one panel was the real
 * risk and worth proving once. It worked, and the arithmetic that follows is the same one
 * `use-block-versions` and `use-scenario-follower` already record: four more blocks, each a
 * chance to get `effIndex`'s clamp or the migrate seed subtly different, and the copies drift.
 *
 * ## The two things that are easy to get wrong
 *
 * **The seed is the ROW's answer key, not the block's.** "Migrate" means the first branch
 * inherits what is currently correct, and on a follower that is the row — seeding from the
 * block's static options silently discards the row's decision at the exact moment the teacher
 * is adding detail to it.
 *
 * **Untick is a delete.** There is no per-row session bank like the block-level toggle's: a
 * row's set is small and belongs to one variant, and banking per row would need a bank per row.
 * Undo already covers it.
 */
import type { ConditionalRoleBranchLike } from '@agavi/blocks';

/** The two shapes a new branch can start as — see `makeConditionalBranch`, which this mirrors. */
export type SeedMode = 'migrate' | 'fork';

export interface NestedBranching<B> {
  /** True when a scenario row is on screen, so the branch set below is the ROW's. */
  nesting: boolean;
  /** The equation in force — the row's while following, the block's otherwise. */
  effEquation: string | undefined;
  /** Whether branching is ON for whichever scope is in force. */
  effEnabled: boolean;
  /** The branch set in force. Empty rather than undefined, so callers need no second check. */
  effBranches: B[];
  /** The active index, clamped into the set actually on screen. */
  effIndex: number;
  /** The branch the answer editor is pointed at, or undefined when branching is off. */
  effActiveBranch: B | undefined;
}

/**
 * Which branch set is on screen, and which one of it is active.
 *
 * Pure, so a block can call it during render beside its own locals. The WRITES stay with the
 * block: they go through its own row writer, which already knows how to mark a row reviewed.
 */
export function nestedBranching<F>({
  activeScenario, blockEquation, blockBranches, blockEnabled, activeBranchIndex, graded,
}: {
  /** The scenario row on screen, or undefined when this block follows nothing. */
  activeScenario: { conditionalEquation?: string; conditionalBranches?: ConditionalRoleBranchLike<F>[] } | undefined;
  blockEquation: string | undefined;
  blockBranches: ConditionalRoleBranchLike<F>[];
  blockEnabled: boolean;
  activeBranchIndex: number;
  /** Branches are authored exclusively in graded mode, the same gate every other reader uses. */
  graded: boolean;
}): NestedBranching<ConditionalRoleBranchLike<F>> {
  const nesting = !!activeScenario;
  const rowBranches = activeScenario?.conditionalBranches;
  const effBranches = nesting ? (rowBranches ?? []) : blockBranches;
  /* Clamped against the set ACTUALLY on screen: paging to a row with fewer branches than the
     last one would otherwise index past the end and render nothing. */
  const effIndex = Math.min(activeBranchIndex, Math.max(0, effBranches.length - 1));
  const effEnabled = nesting ? rowBranches !== undefined : blockEnabled;
  return {
    nesting,
    effEquation: nesting ? activeScenario.conditionalEquation : blockEquation,
    effEnabled,
    effBranches,
    effIndex,
    effActiveBranch: effEnabled && graded ? effBranches[effIndex] : undefined,
  };
}

/**
 * A new branch for the ROW, seeded from the ROW.
 *
 * `makeConditionalBranch` seeds from the block's own answer key, which is the wrong one here —
 * see the note at the top. `cloneFeedback` and `seedFeedback` are the block's, because what a
 * fresh feedback set looks like is a block's own business.
 */
export function makeRowBranch<F>({ mode, roles, feedback, cloneFeedback, seedFeedback }: {
  mode: SeedMode;
  roles: Record<string, unknown>;
  feedback: F;
  cloneFeedback: (f: F) => F;
  seedFeedback: (f: F) => F;
}): ConditionalRoleBranchLike<F> {
  return {
    id: crypto.randomUUID(),
    operator: '==',
    threshold: '0',
    roles: { ...roles } as ConditionalRoleBranchLike<F>['roles'],
    feedback: mode === 'migrate' ? cloneFeedback(feedback) : seedFeedback(feedback),
  };
}
